/**
 * Loyalty Program Settings - Treueprogramm konfigurieren
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Gift, Sparkles, TrendingUp, Save } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function LoyaltyProgramSettings() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/loyalty-rules/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/loyalty-rules/config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });

      if (response.ok) {
        toast.success('Treueprogramm gespeichert!');
      }
    } catch (error) {
      toast.error('Fehler');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[#0B1220] to-[#0E1628]">
        <div className="text-white">Lädt...</div>
      </div>
    );
  }

  const programTypes = [
    {
      value: 'nth_order_free',
      label: 'Jede X. Bestellung',
      description: 'z.B. Jede 10. Bestellung gratis',
      icon: Gift
    },
    {
      value: 'points',
      label: 'Punkte-System',
      description: 'Punkte sammeln, gegen Rabatt eintauschen',
      icon: TrendingUp
    },
    {
      value: 'stamp_card',
      label: 'Stempelkarte',
      description: 'Klassische Stempelkarte digital',
      icon: Sparkles
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1220] to-[#0E1628] p-6">
      <div className="max-w-4xl mx-auto">
        
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Treueprogramm
          </h1>
          <p className="text-slate-400">
            Automatische Belohnungen für Stammkunden
          </p>
        </div>

        <Card className="bg-[#111C32] border-slate-700/50 mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white">Treueprogramm aktivieren</CardTitle>
                <CardDescription className="text-slate-400">
                  Automatische Rabatte für treue Kunden
                </CardDescription>
              </div>
              <Switch
                checked={config?.enabled || false}
                onCheckedChange={(checked) => setConfig({...config, enabled: checked})}
                className="data-[state=checked]:bg-teal-500"
                data-testid="loyalty-enabled-switch"
              />
            </div>
          </CardHeader>
        </Card>

        {config?.enabled && (
          <>
            {/* Programm-Typ Auswahl */}
            <Card className="bg-[#111C32] border-slate-700/50 mb-6">
              <CardHeader>
                <CardTitle className="text-white">Programm-Typ</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {programTypes.map(({ value, label, description, icon: Icon }) => (
                  <button
                    key={value}
                    onClick={() => setConfig({...config, program_type: value})}
                    className={`
                      w-full p-4 rounded-xl border-2 transition-all text-left
                      ${config.program_type === value
                        ? 'border-teal-500 bg-teal-500/15'
                        : 'border-slate-700/50 bg-slate-800/30 hover:border-slate-600'
                      }
                    `}
                    data-testid={`program-type-${value}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`
                        w-12 h-12 rounded-lg flex items-center justify-center
                        ${config.program_type === value
                          ? 'bg-teal-500/30'
                          : 'bg-slate-700/50'
                        }
                      `}>
                        <Icon className={`w-6 h-6 ${config.program_type === value ? 'text-teal-400' : 'text-slate-400'}`} />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-white mb-1">{label}</div>
                        <div className="text-sm text-slate-400">{description}</div>
                      </div>
                      {config.program_type === value && (
                        <Badge className="bg-teal-500/30 text-teal-300 border-teal-500/50">
                          Ausgewählt
                        </Badge>
                      )}
                    </div>
                  </button>
                ))}
              </CardContent>
            </Card>

            {/* Programm-spezifische Einstellungen */}
            {config.program_type === 'nth_order_free' && (
              <Card className="bg-[#111C32] border-slate-700/50 mb-6">
                <CardHeader>
                  <CardTitle className="text-white">Jede X. Bestellung Konfiguration</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Jede wie vielte Bestellung?</Label>
                      <Input
                        type="number"
                        value={config.nth_order || 10}
                        onChange={(e) => setConfig({...config, nth_order: parseInt(e.target.value)})}
                        className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                        min="2"
                        max="50"
                        data-testid="nth-order-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">Standard: Jede 10.</p>
                    </div>

                    <div>
                      <Label className="text-slate-300">Rabatt (%)</Label>
                      <Input
                        type="number"
                        value={config.discount_percent || 100}
                        onChange={(e) => setConfig({...config, discount_percent: parseFloat(e.target.value)})}
                        className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                        min="10"
                        max="100"
                        step="5"
                        data-testid="discount-percent-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">100% = komplett gratis</p>
                    </div>
                  </div>

                  {/* Beispiel */}
                  <div className="p-4 bg-gradient-to-br from-teal-500/10 to-emerald-500/10 rounded-lg border border-teal-500/30">
                    <div className="text-sm text-slate-300">
                      <strong className="text-teal-400">Beispiel:</strong> Kunde bestellt zum {config.nth_order || 10}. Mal 
                      → Erhält {config.discount_percent || 100}% Rabatt auf diese Bestellung
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {config.program_type === 'points' && (
              <Card className="bg-[#111C32] border-slate-700/50 mb-6">
                <CardHeader>
                  <CardTitle className="text-white">Punkte-System</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="p-6 bg-blue-500/10 rounded-lg border border-blue-500/30 text-center">
                    <Sparkles className="w-12 h-12 mx-auto mb-3 text-blue-400" />
                    <p className="text-slate-300 mb-2">Punkte-System kommt in v2</p>
                    <p className="text-sm text-slate-500">
                      Kunden sammeln Punkte und tauschen gegen Rabatte
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            <Button
              onClick={saveConfig}
              disabled={saving}
              className="w-full h-14 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-white text-lg font-semibold"
              data-testid="save-loyalty-config"
            >
              <Save className="w-5 h-5 mr-2" />
              Treueprogramm speichern
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
